import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import { FiArchive, FiMoreVertical, FiEdit3, FiRefreshCcw, FiArrowRight } from 'react-icons/fi';
import HealthRing from './HealthRing';
import SegmentationPanel from './SegmentationPanel';
import SetupChecklist from './SetupChecklist';

const ProjectCard = ({ project, onArchive, onEdit }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [expandedHealth, setExpandedHealth] = useState(false);

  // Only modules are required to run a session; screener + targets are optional.
  const isSetupComplete = project.modulesCount > 0;

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-md group flex flex-col relative overflow-visible hover:border-rutabaga-pink/20 transition-all duration-300">
      {/* Top Right Actions */}
      <div className="absolute top-4 right-4 z-10">
        <div className="relative">
          <button 
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
            className="p-1.5 text-gray-300 hover:text-rutabaga-dark transition-colors rounded-md hover:bg-gray-50"
          >
            <SafeIcon icon={FiMoreVertical} className="text-sm" />
          </button>

          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)}></div>
              <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-100 rounded-lg shadow-xl z-20 overflow-hidden py-1">
                <button 
                  onClick={(e) => { e.stopPropagation(); setShowMenu(false); onEdit(project); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <SafeIcon icon={FiEdit3} />
                  Edit Details
                </button>
                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setShowMenu(false); 
                    onArchive(project.id, project.status === 'archived' ? 'active' : 'archived'); 
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-xs font-bold transition-colors ${
                    project.status === 'archived' ? 'text-emerald-600 hover:bg-emerald-50' : 'text-rutabaga-pink hover:bg-red-50'
                  }`}
                >
                  <SafeIcon icon={project.status === 'archived' ? FiRefreshCcw : FiArchive} />
                  {project.status === 'archived' ? 'Restore Project' : 'Archive Project'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="p-6 flex-1">
        {/* Header Row */}
        <div className="flex justify-between items-start mb-1 pr-12">
          <h3 className="text-xl font-black text-rutabaga-dark tracking-tight leading-tight group-hover:text-rutabaga-pink transition-colors">
            {project.name}
          </h3>
        </div>

        {/* Status Badge */}
        <div className="mb-4">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
            project.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
          }`}>
            {project.status}
          </span>
        </div>

        <p className="text-gray-400 text-xs font-medium line-clamp-2 min-h-[32px] mb-6">
          {project.description || 'No description provided for this research study.'}
        </p>

        <div className="h-[1px] bg-gray-50 mb-6"></div>

        {/* Middle Section */}
        {!isSetupComplete ? (
          <SetupChecklist projectId={project.id} stats={project} />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <HealthRing current={project.totalActual} target={project.totalTarget} color={project.healthColor} size={60} />
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Recruitment</p>
                  <p className="text-xs font-bold text-gray-500 italic">Target: {project.target_n} interviews</p>
                </div>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); setExpandedHealth(!expandedHealth); }}
                className="text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-rutabaga-dark transition-colors"
              >
                {expandedHealth ? 'Close Stats' : 'View Segments'}
              </button>
            </div>
            {expandedHealth && <SegmentationPanel segments={project.segmentation} />}
          </div>
        )}
      </div>

      {/* Bottom Footer */}
      <div 
        onClick={() => navigate(`/projects/${project.id}`)}
        className="p-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <span className="text-[10px] font-bold text-gray-400">
          Created {format(new Date(project.created_at), 'MMM d, yyyy')}
        </span>
        <span className="text-xs font-black text-rutabaga-pink flex items-center gap-1">
          Open Project <SafeIcon icon={FiArrowRight} />
        </span>
      </div>
    </div>
  );
};

export default ProjectCard;